import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowSquareOut, Copy, MicrosoftOutlookLogo, SignIn, X } from "@phosphor-icons/react";
import { adminApi, getErrorMessage } from "@/lib/api";
import { btnGhost, btnPrimary } from "../ui";
import { ItAdminRequest } from "./ItAdminRequest";

const OUTCOMES = {
  needs_approval: ["amber", "Waiting for your admin's approval", "If Microsoft showed a \"Request approval\" box, send it and Microsoft emails your admin. Otherwise email IT below. When they've approved, press Connect Outlook again."],
  needs_setting: ["amber", "IT needs to switch on one setting", "Microsoft won't finish the sign-in until your IT admin turns on \"Allow public client flows\" for the app. Email IT below, then press Connect Outlook again."],
  declined: ["amber", "Sign-in was cancelled", "Press Connect Outlook to try again."],
  expired: ["amber", "The sign-in code expired", "Codes last 15 minutes. Press Connect Outlook for a new one."],
  wrong_account: ["red", "That was a different account", null],
  error: ["red", "Sign-in didn't finish", null],
};

function Outcome({ r, mailbox }) {
  const [tone, title, text] = OUTCOMES[r.state] || OUTCOMES.error;
  const msg = text || (r.state === "wrong_account" ? `You signed in as ${r.account || "another account"}. Press Connect Outlook and sign in as ${mailbox}.` : r.message);
  const cls = tone === "red" ? "border-red-500/40 bg-red-500/[0.06] text-red-300" : "border-amber-500/40 bg-amber-500/[0.06] text-amber-200";
  return <div className={`border rounded-lg px-3 py-2.5 ${cls}`} data-testid={`wi-outlook-outcome-${r.state}`}><div className="text-sm font-semibold">{title}</div><p className="text-xs opacity-80 mt-0.5 break-words">{msg}</p></div>;
}

function SignInCode({ s, mailbox, onCancel }) {
  const copy = () => navigator.clipboard.writeText(s.user_code).then(() => toast.success("Code copied")).catch(() => toast.error("Couldn't copy"));
  const mins = Math.max(0, Math.round((new Date(s.expires_at) - Date.now()) / 60000));
  return (
    <div className="space-y-3" data-testid="wi-outlook-signin">
      <ol className="text-xs text-neutral-400 space-y-1 list-decimal list-inside">
        <li>Open the Microsoft sign-in page.</li><li>Enter this code.</li><li>Sign in as <b className="text-neutral-200">{mailbox}</b>. This page updates by itself.</li>
      </ol>
      <div className="flex items-center gap-3">
        <div className="font-mono text-2xl font-bold tracking-[0.25em] text-white bg-black/50 border border-[#73bf44]/40 rounded-lg px-4 py-2 select-all" data-testid="wi-outlook-signin-code">{s.user_code}</div>
        <button className={btnGhost} onClick={copy} data-testid="wi-outlook-signin-copy-btn"><Copy size={15} /> Copy</button>
      </div>
      <div className="flex flex-wrap gap-2">
        <a href={s.verification_uri} target="_blank" rel="noopener noreferrer" className={btnPrimary} data-testid="wi-outlook-signin-open-btn"><ArrowSquareOut size={16} /> Open Microsoft sign-in</a>
        <button className={btnGhost} onClick={onCancel} data-testid="wi-outlook-signin-cancel-btn"><X size={15} /> Cancel</button>
      </div>
      <p className="text-[0.7rem] text-neutral-500 animate-pulse" data-testid="wi-outlook-signin-waiting">Waiting for you to sign in · code expires in about {mins} min</p>
    </div>
  );
}

export function OutlookConnect({ o, onChange }) {
  const [sign, setSign] = useState(o.sign_in);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const code = sign?.user_code;
  useEffect(() => {
    if (!code) return undefined;
    let stop = false, timer;
    const check = async () => {
      try {
        const { data } = await adminApi.post("/admin/wi/outlook/connect/check");
        if (stop) return;
        if (data.state === "waiting") { timer = setTimeout(check, (data.interval || 5) * 1000); return; }
        setSign(null);
        setResult(["none", "connected"].includes(data.state) ? null : data);
        if (data.state === "connected") { toast.success("Outlook connected. Approval emails now go into warranty@ as drafts"); onChange((await adminApi.get("/admin/wi/outlook")).data); }
      } catch { if (!stop) timer = setTimeout(check, 10000); }
    };
    timer = setTimeout(check, 4000);
    return () => { stop = true; clearTimeout(timer); };
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    setBusy(true); setResult(null);
    try { setSign((await adminApi.post("/admin/wi/outlook/connect")).data); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  const cancel = async () => { setSign(null); try { onChange((await adminApi.post("/admin/wi/outlook/connect/cancel")).data); } catch { /* the code just expires */ } };
  return (
    <div className="space-y-4">
      <div className="border border-neutral-800 bg-black/30 rounded-xl p-4 space-y-3" data-testid="wi-outlook-connect">
        <div className="text-sm text-neutral-200 font-semibold flex items-center gap-2"><MicrosoftOutlookLogo size={17} className="text-[#73bf44]" /> Connect Outlook</div>
        {result && <Outcome r={result} mailbox={o.mailbox} />}
        {sign ? <SignInCode s={sign} mailbox={o.mailbox} onCancel={cancel} /> : <>
          <p className="text-xs text-neutral-400">Sign in to Microsoft as <b className="text-neutral-200">{o.mailbox}</b>. If your company needs an admin to approve the app first, Microsoft asks them for you.</p>
          <button className={btnPrimary} onClick={start} disabled={busy} data-testid="wi-outlook-connect-btn"><SignIn size={16} /> {busy ? "Starting…" : "Connect Outlook"}</button>
        </>}
      </div>
      <ItAdminRequest mailbox={o.mailbox} links={o.it_links} highlight={["needs_approval", "needs_setting"].includes(result?.state)} />
    </div>
  );
}
